import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Clock, Shield, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { products, cities, services } from '../data/seoPages';

export default function ProductPage() {
  const { slug } = useParams();
  
  const product = products.find(p => p.slug === slug);

  // Update document title
  useEffect(() => {
    if (product) {
      document.title = `${product.name} | Professional Installation Florida | IQES`;
      
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', `${product.description} Professional installation in Florida. Free quotes. Call 786-981-3760.`);
      }
    }
  }, [product, slug]);
  
  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800">Page Not Found</h1>
          <Link to="/" className="text-orange-500 hover:underline mt-4 block">Return to Home</Link>
        </div>
      </div>
    );
  }

  // Get content based on product
  const getContent = () => {
    switch(product.slug) {
      case 'cat5e-vs-cat6':
        return {
          intro: 'Choosing between Cat5e and Cat6 cabling is an important decision for your network infrastructure.',
          sections: [
            { title: 'Cat5e Overview', content: 'Cat5e supports speeds up to 1 Gbps at 100 meters with 100 MHz bandwidth. Cost-effective for basic networking.' },
            { title: 'Cat6 Overview', content: 'Cat6 supports speeds up to 10 Gbps at 55 meters with 250 MHz bandwidth. Better performance and future-proofing.' },
            { title: 'Our Recommendation', content: 'For new installations, we recommend Cat6 or Cat6A for future-proofing.' }
          ]
        };
      case 'cat6-vs-cat6a':
        return {
          intro: 'Cat6A offers significant improvements over standard Cat6 for demanding network environments.',
          sections: [
            { title: 'Cat6 Performance', content: 'Cat6 supports 10 Gbps speeds up to 55 meters with 250 MHz bandwidth.' },
            { title: 'Cat6A Advantages', content: 'Cat6A supports 10 Gbps at full 100 meters with 500 MHz bandwidth.' },
            { title: 'When to Choose Cat6A', content: 'Choose Cat6A for data centers, hospitals, or high-density environments.' }
          ]
        };
      default:
        return {
          intro: product.description,
          sections: [
            { title: 'Professional Installation', content: 'Our certified technicians have extensive experience installing these systems.' },
            { title: 'Quality Equipment', content: 'We use only commercial-grade equipment from trusted manufacturers.' },
            { title: 'Warranty & Support', content: 'All installations come with warranty coverage and ongoing support.' }
          ]
        };
    }
  };

  const content = getContent();

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0a1628] via-[#1a2d4a] to-[#0f2341] text-white py-20 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full mb-6">
              <span className="text-sm font-medium">Product Guide</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {product.name}
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              {product.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg">
                  Get Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+17869813760">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  <Phone className="mr-2 w-5 h-5" />
                  786-981-3760
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-600 mb-8">{content.intro}</p>
            
            {content.sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="mb-8"
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
                <p className="text-gray-600">{section.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Why Choose IQES?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">13+ Years Experience</h3>
                <p className="text-gray-600">Trusted experts in low voltage</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">Licensed & Insured</h3>
                <p className="text-gray-600">Fully licensed Florida contractor</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">Free Quotes</h3>
                <p className="text-gray-600">No obligation estimates</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Service Areas in Florida
            </h2>
            
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {cities.slice(0, 10).map((city) => (
                <Link 
                  key={city.slug}
                  to={`/cctv-installation-${city.slug}`}
                  className="px-4 py-2 rounded-full bg-gray-100 text-gray-700 hover:bg-orange-100 transition-all"
                >
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">
              Related Services
            </h2>
            
            <div className="flex flex-wrap justify-center gap-3">
              {services.slice(0, 6).map((service) => (
                <Link 
                  key={service.slug}
                  to={`/${service.slug}-miami`}
                  className="px-4 py-2 rounded-full bg-white text-gray-700 hover:bg-orange-100 transition-all shadow-sm"
                >
                  {service.shortName}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Contact us today for a free consultation
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button size="lg" className="bg-white text-orange-500 hover:bg-gray-100 px-8 py-6 text-lg">
                Request Free Quote
              </Button>
            </Link>
            <a href="tel:+17869813760">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                Call 786-981-3760
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
